import type { Camelized } from 'humps';

import { AgreementStatus } from 'constants/agreements';
import { StripePaymentStatus, MarkAsPaidStatus, RefundReason, ChargeCategory, PER_UNIT } from 'constants/charges';
import { Currency } from 'constants/common';
import { UnitSystem } from 'constants/units';
import { TelematicsStatus } from 'constants/telematics';
import { GeofenceActionType, GeofenceType, UserGeofenceType } from 'constants/geofence';
import { HistoryAction, HistoryCategory } from 'constants/vehicles';

export type ListResource<T> = {
  count: number;
  next?: number;
  previous?: number;
  results: T[];
};

export enum Language {
  FR = 'fr',
  EN = 'en',
}

export enum PaymentMethods {
  CASH = 'cash',
  DEBIT = 'debit',
  RO = 'ro',
  CREDIT = 'credit',
  STRIPE = 'stripe',
  INTERNAL = 'internal',
}

export enum CreditCardBrand { // Must match CC_BRANDS in Back-end
  VISA = 'Visa',
  MASTERCARD = 'MasterCard',
  AMERICAN_EXPRESS = 'American Express',
  DISCOVER = 'Discover',
  JCB = 'JCB',
  DINERS_CLUB = 'Diners Club',
  UNION_PAY = 'UnionPay',
}

export enum BrandingTypes {
  FLEETLANE = 'fleetlane',
  WEEVE = 'weeve',
  AVIGO = 'avigo',
}

export type WithDatabaseID<T, V = number> = T & { id: V };

// ##############################
// ###       RESOURCES        ###
// ##############################

// Dealership Settings

export type DealershipSettingsResource = {
  address1: string;
  address2: string;
  beta_features_enabled: boolean;
  branding_type: BrandingTypes;
  charges_fuel_unit: string;
  city: string;
  country: string;
  customer_contact_interval: string;
  currency: Currency;
  dealership_fuel_unit: string;
  dealership_odometer_unit: string;
  dms_customer_name: string | null;
  email_font_name: string;
  email_subdomain: string | null;
  id: number;
  inbound_phone_number: string | null;
  inbound_email: string | null;
  kimoby_ready_to_use: boolean;
  latitude: number | null;
  longitude: number | null;
  notification_overdue_hours_limit: number;
  payment_account_id: string | null;
  payments_accepted_terms_date: string | null;
  payments_accepted_terms_ip: string | null;
  payments_accepted_terms_user: number | null;
  payments_accepted_terms: boolean;
  payments_enabled: boolean;
  plan_id: number;
  postal_code: string;
  reply_forwarding_delay: string;
  sms_phone_number: string | null;
  state: string;
  street_address: string;
  timezone: string;
  website: string | null;
};

export type DealershipSettings = Camelized<DealershipSettingsResource>;

export type DealershipDepartmentResource = {
  id: number;
  name: string;
};

export type DealershipDepartment = Camelized<DealershipDepartmentResource>;

// Auth
export type AuthUserProfileResource = {
  id: number;
  dealership: {
    id: number;
    settings: DealershipSettingsResource;
    name: string;
  };
  department: number | null;
  role: number | null;
  language: string;
};

export type AuthUserProfile = Camelized<AuthUserProfileResource>;

export type AuthUserResource = {
  intercom_hash: string;
  id: number;
  is_active: boolean;
  first_name: string | null;
  last_name: string | null;
  email: string;
  profile: AuthUserProfileResource;
};

export type AuthUser = Camelized<AuthUserResource>;

export type AuthUserWithoutProfileResource = {
  id: number;
  is_active: boolean;
  first_name: string | null;
  last_name: string | null;
  email: string;
};

export type AuthUserWithoutProfile = Camelized<AuthUserWithoutProfileResource>;

export type UserAccessResource = {
  customer?: number;
  dealership_settings?: number;
};

export type UserAccess = Camelized<UserAccessResource>;

// Transactions

export type PaymentMethod = {
  date: string;
  reference: string;
};

export type ExternalPaymentMethod = PaymentMethod & {
  status: MarkAsPaidStatus;
};

export type CashPaymentMethod = ExternalPaymentMethod & {
  method: 'cash';
};

export type DebitPaymentMethod = ExternalPaymentMethod & {
  method: 'debit';
};

export type CreditPaymentMethod = ExternalPaymentMethod & {
  method: 'credit';
};

export type ROPaymentMethod = ExternalPaymentMethod & {
  method: 'ro';
};

export type StripePaymentMethod = PaymentMethod & {
  method: 'stripe';
  status: StripePaymentStatus;
  card_last4: string;
  card_brand: CreditCardBrand;
  status_code: string | null;
};

export type InternalPaymentMethod = ExternalPaymentMethod & {
  method: 'internal';
};

export type AnyPaymentMethod = Camelized<
  | CashPaymentMethod
  | DebitPaymentMethod
  | CreditPaymentMethod
  | ROPaymentMethod
  | StripePaymentMethod
  | InternalPaymentMethod
>;

export type TransactionResource<P = AnyPaymentMethod> = {
  id: number;
  charges: number[];
  amount: number;
  currency: Currency;
  amount_refunded: number;
  disputed: boolean;
  refunds: number[];
  payment_method: P;
  agent_name: string;
};

export type Transaction<P = AnyPaymentMethod> = Camelized<TransactionResource<P>>;

export type ChargeTax = {
  code: string;
  rate: number;
};

export type TaxCreationType = ChargeTax & {
  description: string | null;
  tax_number: string;
};

export type TaxResource = WithDatabaseID<TaxCreationType> & {
  seq: number;
};

export type Tax = Camelized<TaxResource>;

export type DefaultChargeCreationResource = {
  autofill: boolean;
  category: ChargeCategory | null;
  code: string;
  description: string;
  is_default_charge: boolean;
  name: string | null;
  overridable: boolean;
  price: number;
  quantity_unit: PER_UNIT | null;
  taxes: TaxResource[];
  dealership: number | null;
};
export type DefaultChargeCreation = Camelized<DefaultChargeCreationResource>;
export type DefaultChargeResource = WithDatabaseID<DefaultChargeCreationResource>;
export type DefaultCharge = Camelized<DefaultChargeResource>;

export type DefaultWarrantyCreationType = DefaultChargeCreationResource & { category: ChargeCategory.WARRANTY };
export type DefaultWarrantyResource = WithDatabaseID<DefaultWarrantyCreationType>;
export type DefaultWarranty = Camelized<DefaultWarrantyResource>;

export type DefaultDiscountCreationType = DefaultChargeCreationResource & { category: ChargeCategory.DISCOUNT };
export type DefaultDiscountResource = WithDatabaseID<DefaultDiscountCreationType>;
export type DefaultDiscount = Camelized<DefaultDiscountResource>;

export type ChargeCreationResource = Omit<
  DefaultChargeCreationResource,
  'is_default_charge' | 'overridable' | 'taxes'
> & {
  quantity: number;
  note: string | null;
  base_charge: number | null;
};
``;
export type ChargeCreation = Camelized<ChargeCreationResource>;

export type ChargeResource = WithDatabaseID<ChargeCreationResource> & {
  taxes: ChargeTax[];
  agreementId: number;
  overridable: boolean;
};

export type Charge = Camelized<ChargeResource>;

export type RefundCreationType = {
  amount: number;
  note: string;
  reason: RefundReason | null;
  transaction: number;
};

export type RefundResource = WithDatabaseID<RefundCreationType> & {
  agent: AuthUserWithoutProfile;
  processed_date: string;
};

export type Refund = Camelized<RefundResource>;

// Agreement

type ResourceAgent = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
};

export type Agent = Camelized<ResourceAgent>;

type ResourceConnectedMetadata = {
  battery_level: number | null;
  battery_voltage: number | null;
  connected_fuel_level_date: string | null;
  connected_fuel_level: number | null;
  connected_battery_level_date: string | null;
  connected_battery_level: number | null;
  connected_odometer_date: string | null;
  connected_odometer: number | null;
  dtcs: string | null;
  fuel_level: number | null;
  fuel_level_stale: boolean | null;
  ignition: boolean;
  imei: string | null;
  is_connected: boolean;
  last_event_date: string | null;
  latitude: string | null;
  location_date: string | null;
  longitude: string | null;
  mil: boolean;
  odometer: number | null;
  odometer_initial_date: string | null;
  odometer_initial: number | null;
  odometer_stale: boolean | null;
  reported_odometer: number | null;
  reported_odometer_date: string | null;
  velocity: number | null;
};

export type ConnectedMetadata = Camelized<ResourceConnectedMetadata>;

type ResourceDamagePicture = {
  added_by: string;
  added_date: string;
  damage: number;
  name: string;
  id: number;
  image: string;
  image_medium: string;
  image_thumbnail: string;
};

export type DamagePicture = Camelized<ResourceDamagePicture>;

type ResourceDamage = {
  comment: string | null;
  date_created: string;
  date_modified: string;
  id: number;
  vehicle: number;
  part: string;
  pictures: ResourceDamagePicture[];
  source: string;
};

export type Damage = Camelized<ResourceDamage>;

type ResourceItemizedTax = {
  amount: number;
  description: string | null;
  code: string;
  rate: number;
};

export type ItemizedTax = Camelized<ResourceItemizedTax>;

interface ResourceAgreementChargeBaseFields {
  id: number;
  taxes: ChargeTax[];
  price: number;
  code: string;
  description: string | null;
  name: string | null;
  quantity_unit: PER_UNIT | '';
  category: ChargeCategory | null;
  quantity: number;
  note: string | null;
  order: number | null;
}

export type AgreementChargeBaseFields = Camelized<ResourceAgreementChargeBaseFields>;

export interface ResourceLatentCharge extends ResourceAgreementChargeBaseFields {
  base_charge: number | null;
  agreement: number;
}

export type LatentCharge = Camelized<ResourceLatentCharge>;
/**
 * @deprecated Will be removed in a coming version of the back-end
 * These reflect the charge saved on the agreement directly.
 */
export interface ResourceAgreementCharge extends ResourceAgreementChargeBaseFields {
  is_default_charge: boolean;
  price_unit: string;
  agreement_id: number;
  is_selected: boolean;
  paid: boolean;
  paid_date: string | null;
  transaction: string | null;
  subtotal: number;
  overridable: boolean;
  autofill: boolean;
}

/**
 * @deprecated Will be removed in a coming version of the back-end
 * These reflect the charge saved on the agreement directly.
 */
export type AgreementCharge = Camelized<ResourceAgreementCharge>;

export enum SignatureStatus {
  pending = 'pending',
  signed = 'signed',
}

export enum CustomerDocumentType {
  LICENSE = 'license',
  INSURANCE = 'insurance',
  PASSPORT = 'passport',
}

export type DocumentPictureResource = {
  image: string;
  image_medium: string;
  image_thumbnail: string;
  page: number;
};

export type CustomerDocumentResource = {
  type: CustomerDocumentType;
  image: DocumentPictureResource[];
};

export type CustomerDocument = Camelized<CustomerDocumentResource>;

type ResourceAgreementCustomer = {
  additional_driver_address_1: string | null;
  additional_driver_address_2: string | null;
  additional_driver_city: string | null;
  additional_driver_country: string | null;
  additional_driver_date_of_birth: string | null;
  additional_driver_email: string | null;
  additional_driver_first_name: string | null;
  additional_driver_last_name: string | null;
  additional_driver_license_expires: string | null;
  additional_driver_license_number: string | null;
  additional_driver_license_state: string | null;
  additional_driver_phone: string | null;
  additional_driver_postal_code: string | null;
  additional_driver_state: string | null;
  customer_address_1: string | null;
  customer_address_2: string | null;
  customer_business_name: string | null;
  customer_city: string | null;
  customer_country: string | null;
  customer_date_of_birth: string | null;
  customer_dms_id: string | null;
  customer_email: string | null;
  customer_external_id: string | null;
  customer_first_name: string | null;
  customer_insurance_company_name: string | null;
  customer_insurance_policy_expiry: string | null;
  customer_insurance_policy_number: string | null;
  customer_insurance_province: string | null;
  customer_language: string | null;
  customer_last_name: string | null;
  customer_license_expires: string | null;
  customer_license_number: string | null;
  customer_license_state: string | null;
  customer_note: string | null;
  customer_notification_preference: string | null;
  customer_phone_business: string | null;
  customer_phone_cell: string | null;
  customer_phone_residential: string | null;
  customer_postal_code: string | null;
  customer_sex: string | null;
  customer_source: number | null;
  customer_state: string | null;
  customer_license: CustomerDocumentResource | null;
  customer_insurance: CustomerDocumentResource | null;
};

export type AgreementCustomer = Camelized<ResourceAgreementCustomer>;

export enum FuelType {
  GASOLINE = 'gasoline',
  DIESEL = 'diesel',
  ELECTRIC = 'electric',
  HYBRID = 'hybrid',
}

export type ResourceAgreement = object &
  ResourceAgreementCustomer & {
    advisor_first_name: null;
    advisor_id: null;
    advisor_last_name: null;
    agent_in: ResourceAgent | null;
    agent_out: ResourceAgent | null;
    balance_due: number;
    balance_paid: number;
    battery_level_in: number | null;
    battery_level_out: number | null;
    charge_taxes: boolean;
    charges: ResourceAgreementCharge[]; // Will be removed in future implementations
    connected_metadata: ResourceConnectedMetadata;
    creditcard_brand: string | null;
    creditcard_exp_month: number | null;
    creditcard_exp_year: number | null;
    creditcard_fingerprint: string | null;
    creditcard_last4: string | null;
    creditcard_postal_code: string | null;
    creditcard_source: number | null;
    customer_signature: string | null;
    customer_signature_ip: string | null;
    customer_signature_status: SignatureStatus | null;
    customer_signature_timestamp: string | null;
    damages_in: ResourceDamage[] | null;
    damages_out: ResourceDamage[] | null;
    date_due: string | null;
    date_in: string | null;
    date_out_changed: string | null;
    date_out: string | null;
    days_open: number | null;
    days_overdue: number | null;
    dealership: number;
    department: number | null;
    discount_total?: number;
    fuel_in: number | null;
    fuel_out: number | null;
    fuel_used: number;
    id: number;
    itemized_taxes: ResourceItemizedTax[];
    km_allowed: number | null;
    latent_charges?: ResourceLatentCharge[];
    manually_notified_agent: number | null;
    manually_notified_date: string | null;
    message: { status: string; updatedAt: string } | null;
    odometer_in: number | null;
    odometer_out: number | null;
    overdue_notification_hours: number;
    private_notes: string | null;
    propagate_data: boolean;
    public_notes: string | null;
    ro_id: string | null;
    status: AgreementStatus;
    secondary_signature: string | null;
    secondary_signature_status: SignatureStatus | null;
    secondary_signature_timestamp: string | null;
    secondary_signature_ip: string | null;
    subtotal: number;
    total_driven: number;
    total: number;
    transactions?: number[];
    trips: string[];
    vehicle_availability: number | null;
    vehicle_battery_level: number | null;
    vehicle_body: string | null;
    vehicle_department: string | null;
    vehicle_exterior_color: string | null;
    vehicle_fuel_level: number | null;
    vehicle_fuel_tank_capacity: number | null;
    vehicle_fuel_type: FuelType | null;
    vehicle_fuel_unit: string;
    vehicle_interior_color: string | null;
    vehicle_license_plate: string | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    vehicle_odometer_unit: string;
    vehicle_odometer: number | null;
    vehicle_source: number | null;
    vehicle_status: number | null;
    vehicle_stock_number: string | null;
    vehicle_tag_number: string | null;
    vehicle_vin: string | null;
    vehicle_year: number | null;
  };

export type Agreement = Camelized<ResourceAgreement>;

export type AgreementFinancialData = Pick<Agreement, 'subtotal' | 'total' | 'balanceDue' | 'itemizedTaxes'>;

export type DateObject = {
  date: string;
  month: string;
  year: string;
};

export type ReduxAgreement = Omit<
  Agreement,
  | 'customerLicenseExpires'
  | 'customerDateOfBirth'
  | 'additionalDriverLicenseExpires'
  | 'additionalDriverDateOfBirth'
  | 'customerInsurancePolicyExpiry'
> & {
  customerLicenseExpires: DateObject;
  customerDateOfBirth: DateObject;
  additionalDriverLicenseExpires: DateObject;
  additionalDriverDateOfBirth: DateObject;
  customerInsurancePolicyExpiry: DateObject;
};

// Customer
export type CreditCardResource = {
  id: number;
  exp_month: number;
  exp_year: number;
  last4: string;
  brand: string;
  created: string;
  updated: string;
  owner: number;
  postal_code: string;
};

export type CustomerResource = {
  id: number;
  dms_id: string;
  first_name: string;
  last_name: string;
  email: string;
  sex: string | null;
  address_1: string;
  address_2: string;
  postal_code: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  business_name: string;
  license_number: string | null;
  license_expires: string | null;
  license_state: string | null;
  license_front: string | null;
  license_back: string | null;
  phone_cell: string | null;
  phone_business: string | null;
  phone_residential: string | null;
  date_of_birth: string | null;
  language: string;
  notification_preference: string;
  insurance_province: string | null;
  insurance_policy_expiry: string | null;
  insurance_policy_number: string | null;
  insurance_company_name: string | null;
  insurance_front: string | null;
  insurance_back: string | null;
  note: string | null;
  created: string;
  modified: string;
  created_in_fleet: boolean;
  default_creditcard: CreditCardResource;
  dealership: number;
};

export type Customer = Camelized<CustomerResource>;

export interface BasicCustomerResource
  extends Pick<CustomerResource, 'id' | 'first_name' | 'last_name' | 'phone_cell'> {
  full_name: string | null;
}

export type BasicCustomer = Camelized<BasicCustomerResource>;

export type CustomerCheckInResource = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  address_1: string;
  address_2: string;
  postal_code: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  license_number: string | null;
  license_expires: string | null;
  license_state: string | null;
  phone_cell: string | null;
  phone_business: string | null;
  phone_residential: string | null;
  date_of_birth: string | null;
  language: string;
  notification_preference: string;
  insurance_province: string | null;
  insurance_policy_expiry: string | null;
  insurance_policy_number: string | null;
  insurance_company_name: string | null;
  note: string | null;
};

export type CustomerCheckIn = Camelized<CustomerCheckInResource>;

// Vehicle
export type DecodedVinResource = {
  make: string | null;
  model: string | null;
  year: string | null;
  fuelTankCapacity: number | null;
  fuelType: FuelType | null;
};

export type DecodedVin = Camelized<DecodedVinResource>;

export enum OdometerStrategy {
  REPORTED = 'reported',
  CALCULATED = 'calculated',
}

export enum ChargeCalculator {
  LINE_ITEM = 'line-item',
  TRANSACTION = 'transaction',
}

export interface FleetSettingsResource {
  allow_taxes_per_agreement: boolean;
  apply_taxes: boolean;
  availability_buffer: number;
  battery_low: null;
  charge_calculator?: ChargeCalculator;
  default_day_out: number;
  default_language: string;
  enable_geofencing: boolean;
  force_vin: boolean;
  geofences: any[];
  high_rpm_threshold: number;
  high_speed_threshold: number;
  hybrid_payment_entry: boolean;
  id: number;
  preferred_odometer_strategy: OdometerStrategy;
  stripe_managed_creditcards: boolean;
  turn_back_miles: number;
  two_signature_enabled: boolean;
  unit_system: UnitSystem;
  require_insurance_on_agreement: boolean;
  require_credit_card_on_agreement: boolean;
  require_checkout_signature: boolean;
}

export type FleetSettings = Camelized<FleetSettingsResource>;

export interface PreviewCalculationResource {
  balance_due: number;
  balance_paid: number;
  charges: AgreementCharge[];
  total: number;
  discount_total: number;
  itemized_taxes: ItemizedTax[];
  subtotal: number;
}

export type PreviewCalculation = Camelized<PreviewCalculationResource>;

export type StripeProvider = {
  paymentMethod: PaymentMethods.STRIPE;
};

export type CashProvider = {
  paymentMethod: PaymentMethods.CASH;
  reference?: string;
};

export type ROProvider = {
  paymentMethod: PaymentMethods.RO;
  reference?: string;
};

export type DebitProvider = {
  paymentMethod: PaymentMethods.DEBIT;
  reference?: string;
};

export type CreditProvider = {
  paymentMethod: PaymentMethods.CREDIT;
  reference?: string;
};

export type InternalProvider = {
  paymentMethod: PaymentMethods.INTERNAL;
  reference?: string;
};

export type ChargeProviders =
  | StripeProvider
  | CashProvider
  | ROProvider
  | DebitProvider
  | CreditProvider
  | InternalProvider;

export enum SignatureType {
  primary = 'primary',
  secondary = 'secondary',
}

export type Recipient = {
  email?: string;
  sms?: string;
};

export type GroupType = {
  id: number | null;
  dealership: number;
  name: string | null;
};

export type DeviceVehicleTypeResource = {
  vehicle: number;
  battery_voltage: number | null;
  connected: boolean;
  fuel_level: number | null;
  fuel_level_date: string | null;
  ignition: boolean;
  latitude: number | null;
  longitude: number | null;
  mil: boolean;
  mileage_calculated: number | null;
  mileage_calculated_date: string | null;
  odometer_calculated: number | null;
  odometer_initial: number | null;
  odometer_initial_date: string | null;
  updated_date: string;
  velocity: number | null;
  vin: string;
};

export type DeviceVehicleType = Camelized<DeviceVehicleTypeResource>;

export type DevicesTypeResource = {
  id: number;
  imei: string | null;
  group: GroupType;
  vehicles: DeviceVehicleTypeResource[];
  status: TelematicsStatus;
  latest_vin: string | null;
  last_update: string | null;
  disabled: boolean;
};

export type DevicesType = Camelized<DevicesTypeResource>;

export type GeofenceAlertTypeResource = {
  created_at: string;
  geofence: string;
  action: string;
};

export type GeofenceAlertType = Camelized<GeofenceAlertTypeResource>;

export type WorldGeofenceResource = {
  id: number;
  name: string;
  nameFr: string;
  isoCode: string;
  geofenceType: GeofenceType.WORLD;
};

export type WorldGeofenceRecord = Camelized<WorldGeofenceResource>;

export type CircleGeofence = {
  type: UserGeofenceType.CIRCLE;
  radius: number;
  center: [number, number];
  edge: [number, number];
};

export type PolygonGeofence = {
  type: UserGeofenceType.POLYGON;
  polygon: [number, number][];
};

export type UserDefinedGeofence = PolygonGeofence | CircleGeofence;

export type CircleGeofenceResource = CircleGeofence & {
  id: number;
  name: string;
  dealership: number;
  geofence_type: GeofenceType.USER_DEFINED;
};

export type CircleGeofenceRecord = Camelized<CircleGeofenceResource>;

export type PolygonGeofenceResource = PolygonGeofence & {
  id: number;
  name: string;
  dealership: number;
  geofenceType: GeofenceType.USER_DEFINED;
};

export type PolygonGeofenceRecord = Camelized<PolygonGeofenceResource>;

export type UserDefinedGeofenceResource = CircleGeofenceResource | PolygonGeofenceResource;

export type UserDefinedGeofenceRecord = Camelized<UserDefinedGeofenceResource>;

export type GeofenceRecord = UserDefinedGeofenceRecord | WorldGeofenceRecord;

export type GeofenceViolationAction = {
  type: GeofenceActionType.VIOLATION;
  payload: Record<string, never>; // Always empty object
};

export type GeofenceViolationActionResource = WithDatabaseID<GeofenceViolationAction>;

export type GeofenceViolationActionRecord = Camelized<GeofenceViolationActionResource>;

export type GeofenceLogAction = {
  type: GeofenceActionType.LOG;
  payload: Record<string, never>; // Always empty object
};

export type GeofenceLogActionResource = WithDatabaseID<GeofenceLogAction>;

export type GeofenceLogActionRecord = Camelized<GeofenceLogActionResource>;

export type GeofenceAlertAction = {
  type: GeofenceActionType.ALERT;
  payload: {
    userIds: number[];
  };
};

export type GeofenceAlertActionResource = WithDatabaseID<GeofenceAlertAction>;

export type GeofenceAlertActionRecord = Camelized<GeofenceAlertActionResource>;

export type GeofenceAction = GeofenceViolationAction | GeofenceLogAction | GeofenceAlertAction;
export type GeofenceActionRecord = GeofenceViolationActionRecord | GeofenceLogActionRecord | GeofenceAlertActionRecord;

export type GeofenceRuleResource = {
  id: number;
  dealership: number;
  name: string;
  on_entry: boolean;
  on_exit: boolean;
  geofence: GeofenceRecord;
  actions: GeofenceActionRecord[];
  is_active: boolean;
};

export type GeofenceRule = Camelized<GeofenceRuleResource>;

export type GeofenceRuleCreation = {
  name: string;
  onExit: boolean;
  onEntry: boolean;
  geofence: {
    id: number;
    geofenceType: GeofenceType;
  };
  actions: {
    type: GeofenceActionType;
    payload: Record<string, never>;
  }[];
};

export type ServiceTypeResource = {
  id: number;
  ro_code: string;
  ro_description: string | null;
  ro_line_code: string | null;
  line_code1: string | null;
  manufactorer: string | null;
  cost: number | null;
};

export type ServiceType = Camelized<ServiceTypeResource>;

export type AppointmentResource = {
  id: number;
  agreement: number | null;
  appointment_date: string;
  appointment_group: string[] | null;
  appointment_number: string;
  appointment_taken_date: string | null;
  customer: BasicCustomerResource;
  dealership: number;
  description: string | null;
  estimated_time: string | null;
  is_deleted: boolean;
  needs_loaner: boolean;
  service_types: ServiceTypeResource[];
  vehicle: number | null;
};

export type Appointment = Camelized<AppointmentResource>;

export type VehicleDepartment = {
  id: number;
  name: string;
};

export type VehicleModelResource = {
  id: number;
  make: string;
  model: string;
  year: number;
  fuel_tank_capacity: number | null;
  fuel_type: FuelType | null;
};

export type VehicleModel = Camelized<VehicleModelResource>;

export const vehicleBlockers = {
  GROUNDED: 0,
  MAINTENANCE: 3,
  SOLD: 4,
  MISSING_UNIT: 6,
  MISSING_KEYS: 7,
  LOW_FUEL: 8,
  NEEDS_WASH: 9,
} as const;

export const vehicleStatus = {
  ...vehicleBlockers,
  AVAILABLE: 1,
} as const;

export type VehicleStatus = (typeof vehicleStatus)[keyof typeof vehicleStatus];
export type VehicleBlocker = (typeof vehicleBlockers)[keyof typeof vehicleBlockers];

export type VehicleResource = {
  agreements?: ResourceAgreement[];
  added_to_fleet_by: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  added_to_fleet_date: string | null;
  availability: number;
  battery_level: number | null;
  battery_voltage: number | null;
  body: string | null;
  combined_fuel_level: number | null;
  combined_fuel_level_type: string | null;
  combined_odometer: number | null;
  combined_odometer_type: string | null;
  connected_battery_level: number | null;
  connected_battery_level_date: string | null;
  connected_charging_state: string | null;
  connected_charging_state_date: string | null;
  connected_fuel_level: number | null;
  connected_fuel_level_date: string | null;
  connected_odometer: number | null;
  connected_odometer_date: string | null;
  customer: number | null;
  damages: ResourceDamage[];
  dealership: number;
  department: number | null;
  dms_last_updated: string | null;
  dtcs: string | null;
  exterior_color: string | null;
  fuel_level: number | null;
  fuel_level_stale: boolean | null;
  fuel_unit: string | null;
  id: number;
  ignition: boolean;
  imei: string | null;
  interior_color: string | null;
  is_connected: boolean;
  last_event_date: string | null;
  latitude: number | null;
  license_plate: string | null;
  location_date: string | null;
  longitude: number | null;
  mil: boolean;
  model: VehicleModelResource | null;
  next_agreement_id: number | null;
  next_agreement_expected_date_out: string | null;
  odometer: number | null;
  odometer_initial: number | null;
  odometer_initial_date: string | null;
  odometer_stale: boolean | null;
  odometer_unit: string | null;
  owner: number | null;
  removed_from_fleet_by: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  removed_from_fleet_date: string | null;
  reported_odometer: number | null;
  reported_odometer_date: string | null;
  status: VehicleStatus;
  stock_number: string | null;
  tag_number: string | null;
  unit_type: number;
  vin: string;
};

export type Vehicle = Camelized<VehicleResource>;

export type Vehicles = Vehicle[];

export type ActiveFleetBlockers = {
  [P in VehicleBlocker]: number;
};

export type ActiveFleetResource = {
  available: number;
  departing_today: number;
  on_loan: number;
  returning_today: number;
  blockers: ActiveFleetBlockers;
};

export type ActiveFleet = Camelized<ActiveFleetResource>;

export type RevenueRecord = {
  date: string;
  amount: number;
};

export type RevenueResource = {
  gross_revenue: RevenueRecord[];
  net_revenue: RevenueRecord[];
  stripe_revenue: RevenueRecord[];
};

export type Revenue = Camelized<RevenueResource>;

export type OpenedAgreementsStatsRecord = {
  date: string;
  count: number;
};

export type OpenedAgreementsStatsResource = {
  total_opened_agreements: number;
  opened_agreements: OpenedAgreementsStatsRecord[];
};

export type OpenedAgreementsStats = Camelized<OpenedAgreementsStatsResource>;

export type UtilizationMetrics = {
  seconds_on_fleet: number;
  seconds_on_agreement: number;
  utilization: number;
};

export type VehicleMetricsMap = {
  [vin: string]: UtilizationMetrics;
};

export type UtilizationResource = {
  [date: string]: UtilizationMetrics | VehicleMetricsMap;
};

export type Utilization = Camelized<UtilizationResource>;

export type DataSourceResource = {
  id: number;
  data_source_id: string; // Verify implementation
  account: string;
  channel_id: string; // Verify implementation
  oem_name: string;
};

export type DataSource = Camelized<DataSourceResource>;

export type DataSources = DataSource[];

export enum EnrolmentStatus {
  // Ours
  UNKNOWN = 'unknown',
  // From TSD
  INQUEUE = 'inQueue',
  SUCCEEDED = 'succeeded',
  PARTIALLY_SUCCEEDED = 'partiallySucceeded',
  FAILED = 'failed',
  UNENROLLMENT_PENDING = 'unenrollmentPending',
}

export type EnrolmentVehicle = {
  id: number;
  vin: string;
  make: string;
};

export type EnrolmentResource = {
  id: number;
  vehicle: EnrolmentVehicle;
  data_source: DataSourceResource;
  status: EnrolmentStatus;
  created_at: string;
  updated_at: string;
};

export type NewEnrolmentResource = {
  vehicle: number;
  data_source: number;
};

export type Enrolment = Camelized<EnrolmentResource>;

export type Enrolments = Enrolment[];

export enum HierarchyType {
  SELF = 'self',
  HIERARCHY = 'hierarchy',
}

export type HierarchyEntityResource = {
  id: number;
  name: string;
  parent_id: number | null;
};

export type HierarchyEntity = Camelized<HierarchyEntityResource>;

export interface CategoryActions {
  category: HistoryCategory;
  action: HistoryAction;
}

export interface AgreementCategoryActions {
  category: HistoryCategory.AGREEMENT;
  action: HistoryAction.ADDED | HistoryAction.REMOVED;
}

export interface DetailsCategoryActions {
  category: HistoryCategory.DETAILS;
  action: HistoryAction.UPDATED;
}

export interface GeofenceCategoryActions {
  category: HistoryCategory.GEOFENCE;
  action: HistoryAction.ENTERED | HistoryAction.EXITED;
}

export interface NoteCategoryActions {
  category: HistoryCategory.NOTE;
  action: HistoryAction.NOTE_ADDED;
}

export interface UnitTypeCategoryActions {
  category: HistoryCategory.UNIT_TYPE;
  action: HistoryAction.SET_LOANER | HistoryAction.SET_SHUTTLE;
}

export type VehicleHistoryUser = Pick<AuthUser, 'id' | 'email' | 'firstName' | 'lastName'>;

export type VehicleHistoryVehicle = Pick<Vehicle, 'vin' | 'status'>;

interface VehicleHistoryResourceBase extends CategoryActions {
  id: number;
  date_created: string;
  user?: VehicleHistoryUser;
  vehicle: VehicleHistoryVehicle;
}

export type VehicleHistoryNoteResource = VehicleHistoryResourceBase &
  NoteCategoryActions & {
    data: {
      note: string;
      id: number;
    };
    object_id: number;
  };

export type VehicleHistoryNote = Camelized<VehicleHistoryNoteResource>;

export type VehicleHistoryUpdateResource = VehicleHistoryResourceBase &
  DetailsCategoryActions & {
    data: undefined[];
    object_id: null;
  };

export type VehicleHistoryUpdate = Camelized<VehicleHistoryUpdateResource>;

export type VehicleHistoryUnitResource = VehicleHistoryResourceBase &
  UnitTypeCategoryActions & {
    data: undefined[];
    object_id: null;
  };

export type VehicleHistoryUnit = Camelized<VehicleHistoryUnitResource>;

export type VehicleHistoryGeofenceResource = VehicleHistoryResourceBase &
  GeofenceCategoryActions & {
    data: {
      name: string;
      id: number;
    };
    object_id: number;
  };

export type VehicleHistoryGeofence = Camelized<VehicleHistoryGeofenceResource>;

export type VehicleHistoryAgreementResource = VehicleHistoryResourceBase &
  AgreementCategoryActions & {
    data: undefined[];
    object_id: number;
  };

export type VehicleHistoryAgreement = Camelized<VehicleHistoryAgreementResource>;

export type VehicleHistoryResource =
  | VehicleHistoryNoteResource
  | VehicleHistoryUpdateResource
  | VehicleHistoryGeofenceResource
  | VehicleHistoryAgreementResource
  | VehicleHistoryUnitResource;
